// Reading a .pfx / .p12 file (PKCS #12, RFC 7292) into a signing identity: its certificate and its private key, with the password that
// protects them. Modern files (PBES2: PBKDF2 and AES) use WebCrypto; older ones (what Windows and OpenSSL 1.x write) use the PKCS #12 key
// derivation with triple DES or RC2, which WebCrypto does not offer, so those two ciphers are here.

import { oidOf, parse, stringOf, type Node } from "./der";
import type { SigningIdentity } from "./cms";

const subtle = (): SubtleCrypto => { const s = globalThis.crypto?.subtle; if (!s) throw new Error("Importing a certificate needs a secure (https) page."); return s; };
const buf = (b: Uint8Array): BufferSource => b as unknown as BufferSource;
const concat = (...p: Uint8Array[]): Uint8Array => { const o = new Uint8Array(p.reduce((n, x) => n + x.length, 0)); let q = 0; for (const x of p) { o.set(x, q); q += x.length; } return o; };

const OID = {
    data: "1.2.840.113549.1.7.1", encryptedData: "1.2.840.113549.1.7.6",
    keyBag: "1.2.840.113549.1.12.10.1.1", shroudedKeyBag: "1.2.840.113549.1.12.10.1.2", certBag: "1.2.840.113549.1.12.10.1.3", x509: "1.2.840.113549.1.9.22.1", localKeyId: "1.2.840.113549.1.9.21",
    pbes2: "1.2.840.113549.1.5.13", pbkdf2: "1.2.840.113549.1.5.12",
    p12_3des: "1.2.840.113549.1.12.1.3", p12_2des: "1.2.840.113549.1.12.1.4", p12_rc2_128: "1.2.840.113549.1.12.1.5", p12_rc2_40: "1.2.840.113549.1.12.1.6",
    commonName: "2.5.4.3", email: "1.2.840.113549.1.9.1"
};
const PRF: Record<string, string> = { "1.2.840.113549.2.7": "SHA-1", "1.2.840.113549.2.8": "SHA-224", "1.2.840.113549.2.9": "SHA-256", "1.2.840.113549.2.10": "SHA-384", "1.2.840.113549.2.11": "SHA-512" };
const DIGEST: Record<string, string> = { "1.3.14.3.2.26": "SHA-1", "2.16.840.1.101.3.4.2.1": "SHA-256", "2.16.840.1.101.3.4.2.2": "SHA-384", "2.16.840.1.101.3.4.2.3": "SHA-512" };
const AES: Record<string, number> = { "2.16.840.1.101.3.4.1.2": 16, "2.16.840.1.101.3.4.1.22": 24, "2.16.840.1.101.3.4.1.42": 32 };

// ---- DES (FIPS 46-3), enough for triple-DES CBC decryption -------------------------------------------------------------------------------

const IP = [58, 50, 42, 34, 26, 18, 10, 2, 60, 52, 44, 36, 28, 20, 12, 4, 62, 54, 46, 38, 30, 22, 14, 6, 64, 56, 48, 40, 32, 24, 16, 8, 57, 49, 41, 33, 25, 17, 9, 1, 59, 51, 43, 35, 27, 19, 11, 3, 61, 53, 45, 37, 29, 21, 13, 5, 63, 55, 47, 39, 31, 23, 15, 7];
const FP = [40, 8, 48, 16, 56, 24, 64, 32, 39, 7, 47, 15, 55, 23, 63, 31, 38, 6, 46, 14, 54, 22, 62, 30, 37, 5, 45, 13, 53, 21, 61, 29, 36, 4, 44, 12, 52, 20, 60, 28, 35, 3, 43, 11, 51, 19, 59, 27, 34, 2, 42, 10, 50, 18, 58, 26, 33, 1, 41, 9, 49, 17, 57, 25];
const EXP = [32, 1, 2, 3, 4, 5, 4, 5, 6, 7, 8, 9, 8, 9, 10, 11, 12, 13, 12, 13, 14, 15, 16, 17, 16, 17, 18, 19, 20, 21, 20, 21, 22, 23, 24, 25, 24, 25, 26, 27, 28, 29, 28, 29, 30, 31, 32, 1];
const PERM = [16, 7, 20, 21, 29, 12, 28, 17, 1, 15, 23, 26, 5, 18, 31, 10, 2, 8, 24, 14, 32, 27, 3, 9, 19, 13, 30, 6, 22, 11, 4, 25];
const PC1 = [57, 49, 41, 33, 25, 17, 9, 1, 58, 50, 42, 34, 26, 18, 10, 2, 59, 51, 43, 35, 27, 19, 11, 3, 60, 52, 44, 36, 63, 55, 47, 39, 31, 23, 15, 7, 62, 54, 46, 38, 30, 22, 14, 6, 61, 53, 45, 37, 29, 21, 13, 5, 28, 20, 12, 4];
const PC2 = [14, 17, 11, 24, 1, 5, 3, 28, 15, 6, 21, 10, 23, 19, 12, 4, 26, 8, 16, 7, 27, 20, 13, 2, 41, 52, 31, 37, 47, 55, 30, 40, 51, 45, 33, 48, 44, 49, 39, 56, 34, 53, 46, 42, 50, 36, 29, 32];
const SHIFTS = [1, 1, 2, 2, 2, 2, 2, 2, 1, 2, 2, 2, 2, 2, 2, 1];
const SBOX = [
    [14, 4, 13, 1, 2, 15, 11, 8, 3, 10, 6, 12, 5, 9, 0, 7, 0, 15, 7, 4, 14, 2, 13, 1, 10, 6, 12, 11, 9, 5, 3, 8, 4, 1, 14, 8, 13, 6, 2, 11, 15, 12, 9, 7, 3, 10, 5, 0, 15, 12, 8, 2, 4, 9, 1, 7, 5, 11, 3, 14, 10, 0, 6, 13],
    [15, 1, 8, 14, 6, 11, 3, 4, 9, 7, 2, 13, 12, 0, 5, 10, 3, 13, 4, 7, 15, 2, 8, 14, 12, 0, 1, 10, 6, 9, 11, 5, 0, 14, 7, 11, 10, 4, 13, 1, 5, 8, 12, 6, 9, 3, 2, 15, 13, 8, 10, 1, 3, 15, 4, 2, 11, 6, 7, 12, 0, 5, 14, 9],
    [10, 0, 9, 14, 6, 3, 15, 5, 1, 13, 12, 7, 11, 4, 2, 8, 13, 7, 0, 9, 3, 4, 6, 10, 2, 8, 5, 14, 12, 11, 15, 1, 13, 6, 4, 9, 8, 15, 3, 0, 11, 1, 2, 12, 5, 10, 14, 7, 1, 10, 13, 0, 6, 9, 8, 7, 4, 15, 14, 3, 11, 5, 2, 12],
    [7, 13, 14, 3, 0, 6, 9, 10, 1, 2, 8, 5, 11, 12, 4, 15, 13, 8, 11, 5, 6, 15, 0, 3, 4, 7, 2, 12, 1, 10, 14, 9, 10, 6, 9, 0, 12, 11, 7, 13, 15, 1, 3, 14, 5, 2, 8, 4, 3, 15, 0, 6, 10, 1, 13, 8, 9, 4, 5, 11, 12, 7, 2, 14],
    [2, 12, 4, 1, 7, 10, 11, 6, 8, 5, 3, 15, 13, 0, 14, 9, 14, 11, 2, 12, 4, 7, 13, 1, 5, 0, 15, 10, 3, 9, 8, 6, 4, 2, 1, 11, 10, 13, 7, 8, 15, 9, 12, 5, 6, 3, 0, 14, 11, 8, 12, 7, 1, 14, 2, 13, 6, 15, 0, 9, 10, 4, 5, 3],
    [12, 1, 10, 15, 9, 2, 6, 8, 0, 13, 3, 4, 14, 7, 5, 11, 10, 15, 4, 2, 7, 12, 9, 5, 6, 1, 13, 14, 0, 11, 3, 8, 9, 14, 15, 5, 2, 8, 12, 3, 7, 0, 4, 10, 1, 13, 11, 6, 4, 3, 2, 12, 9, 5, 15, 10, 11, 14, 1, 7, 6, 0, 8, 13],
    [4, 11, 2, 14, 15, 0, 8, 13, 3, 12, 9, 7, 5, 10, 6, 1, 13, 0, 11, 7, 4, 9, 1, 10, 14, 3, 5, 12, 2, 15, 8, 6, 1, 4, 11, 13, 12, 3, 7, 14, 10, 15, 6, 8, 0, 5, 9, 2, 6, 11, 13, 8, 1, 4, 10, 7, 9, 5, 0, 15, 14, 2, 3, 12],
    [13, 2, 8, 4, 6, 15, 11, 1, 10, 9, 3, 14, 5, 0, 12, 7, 1, 15, 13, 8, 10, 3, 7, 4, 12, 5, 6, 11, 0, 14, 9, 2, 7, 11, 4, 1, 9, 12, 14, 2, 0, 6, 10, 13, 15, 3, 5, 8, 2, 1, 14, 7, 4, 10, 8, 13, 15, 12, 9, 0, 3, 5, 6, 11]
];

const bitsOf = (b: Uint8Array): number[] => { const o: number[] = []; for (const x of b) for (let i = 7; i >= 0; i--) o.push((x >> i) & 1); return o; };
const bytesOf = (bits: number[]): Uint8Array => { const o = new Uint8Array(bits.length / 8); for (let i = 0; i < bits.length; i++) o[i >> 3]! |= bits[i]! << (7 - (i & 7)); return o; };
const permute = (src: number[], table: number[]) => table.map((t) => src[t - 1]!);

/** The sixteen 48-bit round keys of one 8-byte DES key. */
function desKeys(key: Uint8Array): number[][] {
    const k = permute(bitsOf(key), PC1);
    let c = k.slice(0, 28), d = k.slice(28);
    const out: number[][] = [];
    for (const s of SHIFTS) {
        c = [...c.slice(s), ...c.slice(0, s)];
        d = [...d.slice(s), ...d.slice(0, s)];
        out.push(permute([...c, ...d], PC2));
    }
    return out;
}

function desBlock(block: Uint8Array, keys: number[][], decrypt: boolean): Uint8Array {
    const b = permute(bitsOf(block), IP);
    let l = b.slice(0, 32), r = b.slice(32);
    for (let i = 0; i < 16; i++) {
        const k = keys[decrypt ? 15 - i : i]!;
        const e = permute(r, EXP).map((x, j) => x ^ k[j]!);
        const f: number[] = [];
        for (let s = 0; s < 8; s++) {
            const six = e.slice(s * 6, s * 6 + 6);
            const row = (six[0]! << 1) | six[5]!, col = (six[1]! << 3) | (six[2]! << 2) | (six[3]! << 1) | six[4]!;
            const v = SBOX[s]![row * 16 + col]!;
            f.push((v >> 3) & 1, (v >> 2) & 1, (v >> 1) & 1, v & 1);
        }
        const pf = permute(f, PERM);
        const nr = l.map((x, j) => x ^ pf[j]!);
        l = r;
        r = nr;
    }
    return bytesOf(permute([...r, ...l], FP));
}

/** Triple DES (EDE, three keys) in CBC mode, decrypting; the padding is PKCS #7. */
function des3CbcDecrypt(key: Uint8Array, iv: Uint8Array, data: Uint8Array): Uint8Array {
    if (data.length % 8) throw new Error("The encrypted data is the wrong length.");
    const k1 = desKeys(key.subarray(0, 8)), k2 = desKeys(key.subarray(8, 16)), k3 = desKeys(key.subarray(16, 24));
    const out = new Uint8Array(data.length);
    let prev = iv;
    for (let p = 0; p < data.length; p += 8) {
        const c = data.subarray(p, p + 8);
        const m = desBlock(desBlock(desBlock(c, k3, true), k2, false), k1, true);
        for (let i = 0; i < 8; i++) out[p + i] = m[i]! ^ prev[i]!;
        prev = c;
    }
    return unpad(out, 8);
}

// ---- RC2 (RFC 2268) ----------------------------------------------------------------------------------------------------------------

const PI_TABLE = new Uint8Array([217, 120, 249, 196, 25, 221, 181, 237, 40, 233, 253, 121, 74, 160, 216, 157, 198, 126, 55, 131, 43, 118, 83, 142, 98, 76, 100, 136, 68, 139, 251, 162, 23, 154, 89, 245, 135, 179, 79, 19, 97, 69, 109, 141, 9, 129, 125, 50, 189, 143, 64, 235, 134, 183, 123, 11, 240, 149, 33, 34, 92, 107, 78, 130, 84, 214, 101, 147, 206, 96, 178, 28, 115, 86, 192, 20, 167, 140, 241, 220, 18, 117, 202, 31, 59, 190, 228, 209, 66, 61, 212, 48, 163, 60, 182, 38, 111, 191, 14, 218, 70, 105, 7, 87, 39, 242, 29, 155, 188, 148, 67, 3, 248, 17, 199, 246, 144, 239, 62, 231, 6, 195, 213, 47, 200, 102, 30, 215, 8, 232, 234, 222, 128, 82, 238, 247, 132, 170, 114, 172, 53, 77, 106, 42, 150, 26, 210, 113, 90, 21, 73, 116, 75, 159, 208, 94, 4, 24, 164, 236, 194, 224, 65, 110, 15, 81, 203, 204, 36, 145, 175, 80, 161, 244, 112, 57, 153, 124, 58, 133, 35, 184, 180, 122, 252, 2, 54, 91, 37, 85, 151, 49, 45, 93, 250, 152, 227, 138, 146, 174, 5, 223, 41, 16, 103, 108, 186, 201, 211, 0, 230, 207, 225, 158, 168, 44, 99, 22, 1, 63, 88, 226, 137, 169, 13, 56, 52, 27, 171, 51, 255, 176, 187, 72, 12, 95, 185, 177, 205, 46, 197, 243, 219, 71, 229, 165, 156, 119, 10, 166, 32, 104, 254, 127, 193, 173]);

function rc2Keys(key: Uint8Array, bits: number): Uint16Array {
    const t8 = (bits + 7) >> 3;
    const tm = 255 % 2 ** (8 + bits - 8 * t8);
    const l = new Uint8Array(128);
    l.set(key);
    for (let i = key.length; i < 128; i++) l[i] = PI_TABLE[(l[i - 1]! + l[i - key.length]!) & 0xff]!;
    l[128 - t8] = PI_TABLE[l[128 - t8]! & tm]!;
    for (let i = 127 - t8; i >= 0; i--) l[i] = PI_TABLE[l[i + 1]! ^ l[i + t8]!]!;
    const k = new Uint16Array(64);
    for (let i = 0; i < 64; i++) k[i] = (l[2 * i + 1]! << 8) | l[2 * i]!;
    return k;
}

const rotr = (x: number, n: number) => ((x >>> n) | (x << (16 - n))) & 0xffff;

function rc2CbcDecrypt(key: Uint8Array, bits: number, iv: Uint8Array, data: Uint8Array): Uint8Array {
    if (data.length % 8) throw new Error("The encrypted data is the wrong length.");
    const k = rc2Keys(key, bits);
    const out = new Uint8Array(data.length);
    let prev = iv;
    for (let p = 0; p < data.length; p += 8) {
        const r = [0, 1, 2, 3].map((i) => data[p + 2 * i]! | (data[p + 2 * i + 1]! << 8));
        let j = 63;
        const rev = () => {
            r[3] = rotr(r[3]!, 5); r[3] = (r[3]! - k[j]! - (r[2]! & r[1]!) - (~r[2]! & r[0]!)) & 0xffff; j--;
            r[2] = rotr(r[2]!, 3); r[2] = (r[2]! - k[j]! - (r[1]! & r[0]!) - (~r[1]! & r[3]!)) & 0xffff; j--;
            r[1] = rotr(r[1]!, 2); r[1] = (r[1]! - k[j]! - (r[0]! & r[3]!) - (~r[0]! & r[2]!)) & 0xffff; j--;
            r[0] = rotr(r[0]!, 1); r[0] = (r[0]! - k[j]! - (r[3]! & r[2]!) - (~r[3]! & r[1]!)) & 0xffff; j--;
        };
        const mash = () => {
            r[3] = (r[3]! - k[r[2]! & 63]!) & 0xffff; r[2] = (r[2]! - k[r[1]! & 63]!) & 0xffff; r[1] = (r[1]! - k[r[0]! & 63]!) & 0xffff; r[0] = (r[0]! - k[r[3]! & 63]!) & 0xffff;
        };
        for (let i = 0; i < 16; i++) { rev(); if (i === 4 || i === 10) mash(); }
        for (let i = 0; i < 4; i++) { out[p + 2 * i] = r[i]! & 0xff; out[p + 2 * i + 1] = r[i]! >> 8; }
        for (let i = 0; i < 8; i++) out[p + i]! ^= prev[i]!;
        prev = data.subarray(p, p + 8);
    }
    return unpad(out, 8);
}

function unpad(b: Uint8Array, block: number): Uint8Array {
    const n = b[b.length - 1] ?? 0;
    if (n < 1 || n > block || n > b.length) throw new WrongPassword();
    for (let i = b.length - n; i < b.length; i++) if (b[i] !== n) throw new WrongPassword();
    return b.subarray(0, b.length - n);
}

export class WrongPassword extends Error {
    constructor() { super("The password is wrong, or the file is not a certificate file."); this.name = "WrongPassword"; }
}

// ---- the PKCS #12 key derivation (RFC 7292 appendix B) -----------------------------------------------------------------------------------

const HASH_SIZES: Record<string, [number, number]> = { "SHA-1": [20, 64], "SHA-256": [32, 64], "SHA-384": [48, 128], "SHA-512": [64, 128] };

async function p12Kdf(hash: string, password: Uint8Array, salt: Uint8Array, iterations: number, id: number, length: number): Promise<Uint8Array> {
    const [u, v] = HASH_SIZES[hash]!;
    const fill = (src: Uint8Array): Uint8Array => { if (!src.length) return new Uint8Array(0); const n = v * Math.ceil(src.length / v); const o = new Uint8Array(n); for (let i = 0; i < n; i++) o[i] = src[i % src.length]!; return o; };
    const D = new Uint8Array(v).fill(id);
    let I = concat(fill(salt), fill(password));
    const out: number[] = [];
    while (out.length < length) {
        let A = new Uint8Array(await subtle().digest(hash, buf(concat(D, I))));
        for (let i = 1; i < iterations; i++) A = new Uint8Array(await subtle().digest(hash, buf(A)));
        out.push(...A.subarray(0, Math.min(u, length - out.length)));
        if (out.length >= length) break;
        const B = new Uint8Array(v);
        for (let i = 0; i < v; i++) B[i] = A[i % u]!;
        // I_j = (I_j + B + 1) mod 2^v for each v-byte block
        const next = new Uint8Array(I.length);
        for (let p = 0; p < I.length; p += v) {
            let carry = 1;
            for (let i = v - 1; i >= 0; i--) { const s = I[p + i]! + B[i]! + carry; next[p + i] = s & 0xff; carry = s >> 8; }
        }
        I = next;
    }
    return Uint8Array.from(out);
}

/** The password as PKCS #12 wants it: UTF-16BE with a terminating zero character (nothing for an empty password). */
const bmp = (pw: string): Uint8Array => { if (!pw) return new Uint8Array(0); const o = new Uint8Array(pw.length * 2 + 2); for (let i = 0; i < pw.length; i++) { o[2 * i] = pw.charCodeAt(i) >> 8; o[2 * i + 1] = pw.charCodeAt(i) & 255; } return o; };

const intOf = (n: Node): number => { let v = 0; for (const b of n.body) v = v * 256 + b; return v; };

/** Decrypts `data` as the AlgorithmIdentifier `alg` says, with the password. */
async function decrypt(alg: Node, data: Uint8Array, pw: string): Promise<Uint8Array> {
    const [o, params] = alg.kids!;
    const id = oidOf(o!);
    if (id === OID.pbes2) {
        const [kdf, scheme] = params!.kids!;
        const [kdfOid, kdfParams] = kdf!.kids!;
        if (oidOf(kdfOid!) !== OID.pbkdf2) throw new Error("This certificate file uses a key derivation that is not supported.");
        const kp = kdfParams!.kids!;
        const salt = kp[0]!.body, iter = intOf(kp[1]!);
        let len = 0, prf = "SHA-1";
        for (const x of kp.slice(2)) { if (x.tag === 0x02) len = intOf(x); else if (x.tag === 0x30) prf = PRF[oidOf(x.kids![0]!)] ?? prf; }
        const [encOid, iv] = scheme!.kids!;
        const keyLen = AES[oidOf(encOid!)];
        if (!keyLen) throw new Error("This certificate file uses a cipher that is not supported.");
        void len;
        const base = await subtle().importKey("raw", buf(new TextEncoder().encode(pw)), "PBKDF2", false, ["deriveBits"]);
        const bits = await subtle().deriveBits({ name: "PBKDF2", hash: prf === "SHA-224" ? "SHA-256" : prf, salt: buf(salt), iterations: iter }, base, keyLen * 8);
        const key = await subtle().importKey("raw", bits, "AES-CBC", false, ["decrypt"]);
        try { return new Uint8Array(await subtle().decrypt({ name: "AES-CBC", iv: buf(iv!.body) }, key, buf(data))); } catch { throw new WrongPassword(); }
    }
    const salt = params!.kids![0]!.body, iter = intOf(params!.kids![1]!);
    const pwd = bmp(pw);
    if (id === OID.p12_3des || id === OID.p12_2des) {
        const key = await p12Kdf("SHA-1", pwd, salt, iter, 1, 24);
        const iv = await p12Kdf("SHA-1", pwd, salt, iter, 2, 8);
        const k = id === OID.p12_2des ? concat(key.subarray(0, 16), key.subarray(0, 8)) : key;
        return des3CbcDecrypt(k, iv, data);
    }
    if (id === OID.p12_rc2_40 || id === OID.p12_rc2_128) {
        const bytes = id === OID.p12_rc2_40 ? 5 : 16;
        const key = await p12Kdf("SHA-1", pwd, salt, iter, 1, bytes);
        const iv = await p12Kdf("SHA-1", pwd, salt, iter, 2, 8);
        return rc2CbcDecrypt(key, bytes * 8, iv, data);
    }
    throw new Error("This certificate file uses an encryption that is not supported.");
}

interface Bag { kind: "key" | "cert"; der: Uint8Array; keyId: string }

async function readSafeContents(der: Uint8Array, pw: string, out: Bag[]): Promise<void> {
    const bags = parse(der)[0]?.kids ?? [];
    for (const bag of bags) {
        const [idN, valN, attrs] = bag.kids!;
        const id = oidOf(idN!);
        let keyId = "";
        for (const a of attrs?.kids ?? []) if (oidOf(a.kids![0]!) === OID.localKeyId) keyId = [...(a.kids![1]!.kids![0]!.body)].map((x) => x.toString(16).padStart(2, "0")).join("");
        const val = valN!.kids![0]!;
        if (id === OID.keyBag) out.push({ kind: "key", der: val.raw, keyId });
        else if (id === OID.shroudedKeyBag) {
            const [alg, enc] = val.kids!;
            out.push({ kind: "key", der: await decrypt(alg!, enc!.body, pw), keyId });
        } else if (id === OID.certBag) {
            const [certType, certVal] = val.kids!;
            if (oidOf(certType!) === OID.x509) out.push({ kind: "cert", der: certVal!.kids![0]!.body, keyId });
        }
    }
}

/** The name and email in a certificate's subject. */
function subjectOf(cert: Uint8Array): { name: string; email: string } {
    let name = "", email = "";
    try {
        const tbs = parse(cert)[0]!.kids![0]!;
        const kids = tbs.kids!;
        const at = kids[0]!.tag === 0xa0 ? 5 : 4;
        for (const rdn of kids[at]?.kids ?? []) for (const atv of rdn.kids ?? []) {
            const [t, v] = atv.kids ?? [];
            if (!t || !v) continue;
            const o = oidOf(t);
            if (o === OID.commonName && !name) name = stringOf(v);
            else if (o === OID.email) email = stringOf(v);
        }
    } catch { /* an unreadable subject is left blank */ }
    return { name, email };
}

/**
 * Reads a PKCS #12 file: its certificate and RSA private key become a signing identity (the key is imported as non-extractable, so it can
 * sign but never be read back out). Throws WrongPassword when the password does not open the file.
 */
export async function importPfx(file: Uint8Array, password: string): Promise<SigningIdentity> {
    let root: Node;
    try { root = parse(file)[0]!; if (root.tag !== 0x30) throw new Error("x"); } catch { throw new Error("This is not a certificate (.pfx / .p12) file."); }
    const [, authSafe, mac] = root.kids!;
    if (!authSafe || oidOf(authSafe.kids![0]!) !== OID.data) throw new Error("This is not a certificate (.pfx / .p12) file.");
    const content = authSafe.kids![1]!.kids![0]!.body;
    // the password: checked against the file's MAC when it has one
    const tries = password === "" ? ["", "\0"] : [password];
    let pw: string | null = null;
    if (mac?.kids) {
        const [digestInfo, macSalt, iterN] = mac.kids;
        const hash = DIGEST[oidOf(digestInfo!.kids![0]!.kids![0]!)] ?? "SHA-1";
        const expect = digestInfo!.kids![1]!.body;
        const iterations = iterN ? intOf(iterN) : 1;
        const [u] = HASH_SIZES[hash]!;
        for (const t of tries) {
            const pwd = t === "\0" ? new Uint8Array(2) : bmp(t);
            const key = await p12Kdf(hash, pwd, macSalt!.body, iterations, 3, u);
            const k = await subtle().importKey("raw", buf(key), { name: "HMAC", hash }, false, ["sign"]);
            const sig = new Uint8Array(await subtle().sign("HMAC", k, buf(content)));
            if (sig.length === expect.length && sig.every((x, i) => x === expect[i])) { pw = t === "\0" ? "" : t; break; }
        }
        if (pw === null) throw new WrongPassword();
    } else pw = password;
    const bags: Bag[] = [];
    for (const ci of parse(content)[0]?.kids ?? []) {
        const [t, c] = ci.kids!;
        const id = oidOf(t!);
        if (id === OID.data) await readSafeContents(c!.kids![0]!.body, pw, bags);
        else if (id === OID.encryptedData) {
            const eci = c!.kids![0]!.kids![1]!;
            const [, alg, enc] = eci.kids!;
            const body = enc!.kids ? concat(...enc!.kids.map((x) => x.body)) : enc!.body;
            await readSafeContents(await decrypt(alg!, body, pw), pw, bags);
        }
    }
    const keyBag = bags.find((b) => b.kind === "key");
    if (!keyBag) throw new Error("There is no private key in this file, so it cannot be used to sign.");
    const certs = bags.filter((b) => b.kind === "cert");
    const cert = certs.find((c) => c.keyId && c.keyId === keyBag.keyId) ?? certs[0];
    if (!cert) throw new Error("There is no certificate in this file.");
    let key: CryptoKey;
    try { key = await subtle().importKey("pkcs8", buf(keyBag.der), { name: "RSASSA-PKCS1-v1_5", hash: "SHA-256" }, false, ["sign"]); } catch { throw new Error("Only RSA keys can be used to sign here."); }
    const { name, email } = subjectOf(cert.der);
    return { name: name || "Imported certificate", email, cert: cert.der, key, created: Date.now() };
}
